import React, { useEffect, useState } from "react";
import { api, fmtEUR, pct } from "../../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import FileUpload from "../../components/FileUpload";
import { X } from "lucide-react";

export default function AthleteDashboard() {
  const [profile, setProfile] = useState(null);
  const [donations, setDonations] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [tab, setTab] = useState("profile");
  const [updateForm, setUpdateForm] = useState({ title: "", body: "", image_url: "" });
  const [edit, setEdit] = useState({});
  const [gallery, setGallery] = useState([]);

  const load = async () => {
    try {
      const [p, d, s] = await Promise.all([
        api.get("/me/athlete"),
        api.get("/me/donations"),
        api.get("/me/sponsorships"),
      ]);
      setProfile(p.data);
      setEdit({
        story: p.data.story || "",
        funding_goal: p.data.funding_goal || 0,
        image_url: p.data.image_url || "",
        video_url: p.data.video_url || "",
      });
      setGallery(Array.isArray(p.data.gallery) ? p.data.gallery : []);
      setDonations(d.data);
      setSponsors(s.data);
    } catch (e) {
      // silent — dashboard shell handles empty profile
    }
  };
  useEffect(() => { load(); }, []);

  if (!profile) return <div className="overline" data-testid="athlete-dash-empty">No athlete profile yet. Reach out to TGI admin.</div>;
  const progress = pct(profile.funding_raised, profile.funding_goal);

  const saveProfile = async () => {
    try {
      await api.put("/me/athlete", {
        ...edit,
        funding_goal: Number(edit.funding_goal),
        gallery,
      });
      toast.success("Profile saved");
      load();
    } catch { toast.error("Save failed"); }
  };

  const addGalleryImage = (url) => {
    if (!url) return;
    setGallery((g) => [...g, url]);
  };

  const removeGalleryImage = (idx) => {
    setGallery((g) => g.filter((_, i) => i !== idx));
  };

  const postUpdate = async () => {
    if (!updateForm.title || !updateForm.body) return;
    try {
      await api.post("/me/updates", updateForm);
      toast.success("Update posted");
      setUpdateForm({ title: "", body: "", image_url: "" });
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="athlete-dashboard">
      <div className="border border-border p-6 mb-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-3">
          {profile.image_url ? (
            <img src={profile.image_url} alt={profile.name} className="w-full aspect-square object-cover border border-border" />
          ) : (
            <div className="w-full aspect-square border border-dashed border-border bg-muted flex items-center justify-center text-foreground/40 text-xs uppercase tracking-[0.18em]" data-testid="athlete-header-placeholder">
              No image
            </div>
          )}
        </div>
        <div className="md:col-span-6">
          <div className="overline">{profile.country} · {profile.tour_level}</div>
          <div className="font-display text-3xl mt-1">{profile.name}</div>
          <div className="mt-4 flex items-baseline justify-between">
            <div>
              <div className="overline">Raised</div>
              <div className="font-display text-3xl">{fmtEUR(profile.funding_raised)}</div>
            </div>
            <div className="text-right">
              <div className="overline">Goal</div>
              <div className="font-display text-xl text-foreground/60">{fmtEUR(profile.funding_goal)}</div>
            </div>
          </div>
          <div className="mt-3 h-1 bg-muted relative">
            <div className="absolute left-0 top-0 h-1 bg-primary" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className="md:col-span-3 flex flex-col gap-2">
          <Link to={`/golfers/${profile.slug || profile.athlete_id}`} className="btn-outline text-center" data-testid="view-public-link">View public profile</Link>
        </div>
      </div>

      <div className="flex gap-0 border-b border-border" data-testid="athlete-tabs">
        {[["profile", "Profile"], ["gallery", "Gallery"], ["updates", "Post update"], ["donations", "Donations"], ["sponsors", "Sponsors"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-5 py-3 text-sm ${tab === k ? "border-b-2 border-primary" : "text-foreground/60"}`} data-testid={`athlete-tab-${k}`}>{l}</button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "profile" && (
          <div className="max-w-2xl space-y-6">
            <FileUpload
              value={edit.image_url}
              onChange={(url) => setEdit({ ...edit, image_url: url })}
              label="Profile image"
              testid="edit-image-upload"
              aspect="aspect-square"
            />
            <label className="block">
              <span className="overline">Story</span>
              <textarea value={edit.story} onChange={(e) => setEdit({ ...edit, story: e.target.value })} rows={6} className="mt-2 w-full p-3 border border-border bg-background outline-none focus:border-primary" data-testid="edit-story" />
            </label>
            <label className="block">
              <span className="overline">Funding goal (EUR)</span>
              <input type="number" value={edit.funding_goal} onChange={(e) => setEdit({ ...edit, funding_goal: e.target.value })} className="mt-2 w-full h-12 px-4 border border-border bg-background outline-none focus:border-primary" data-testid="edit-goal" />
            </label>
            <button onClick={saveProfile} className="btn-primary" data-testid="save-profile-btn">Save profile</button>
          </div>
        )}

        {tab === "gallery" && (
          <div className="max-w-4xl" data-testid="athlete-gallery">
            <div className="overline mb-2">Gallery ({gallery.length})</div>
            <p className="text-sm text-foreground/60 mb-6">Add photos from tournaments, training, or the road. Click Save gallery below to publish your changes.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {gallery.map((url, i) => (
                <div key={`${url}-${i}`} className="relative group border border-border" data-testid={`gallery-tile-${i}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeGalleryImage(i)}
                    className="absolute top-2 right-2 bg-background/90 border border-border p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`gallery-remove-${i}`}
                    aria-label="Remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <FileUpload
                value=""
                onChange={addGalleryImage}
                label=""
                testid="gallery-add"
                aspect="aspect-[4/5]"
              />
            </div>
            <button onClick={saveProfile} className="btn-primary mt-8" data-testid="save-gallery-btn">Save gallery</button>
          </div>
        )}

        {tab === "updates" && (
          <div className="max-w-2xl space-y-4" data-testid="athlete-post-update">
            <input value={updateForm.title} onChange={(e) => setUpdateForm({ ...updateForm, title: e.target.value })} placeholder="Update title" className="w-full h-12 px-4 border border-border bg-background outline-none focus:border-primary" data-testid="update-title" />
            <textarea value={updateForm.body} onChange={(e) => setUpdateForm({ ...updateForm, body: e.target.value })} rows={5} placeholder="What happened?" className="w-full p-3 border border-border bg-background outline-none focus:border-primary" data-testid="update-body" />
            <FileUpload
              value={updateForm.image_url}
              onChange={(url) => setUpdateForm({ ...updateForm, image_url: url })}
              label="Image (optional)"
              testid="update-image-upload"
              aspect="aspect-[3/2]"
            />
            <button onClick={postUpdate} className="btn-primary" data-testid="post-update-btn">Post update</button>
          </div>
        )}

        {tab === "donations" && (
          <div className="border border-border" data-testid="athlete-donations">
            {donations.length === 0 && <div className="p-6 text-foreground/60">No donations yet.</div>}
            {donations.map((d) => (
              <div key={d.donation_id} className="p-4 border-b border-border last:border-b-0 flex items-baseline justify-between">
                <div>
                  <div className="font-medium">{d.anonymous ? "Anonymous" : d.donor_name}</div>
                  <div className="text-xs text-foreground/60">{new Date(d.created_at).toLocaleDateString()} · {d.type}</div>
                </div>
                <div className="font-display text-xl">{fmtEUR(d.amount)}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "sponsors" && (
          <div className="border border-border" data-testid="athlete-sponsors">
            {sponsors.length === 0 && <div className="p-6 text-foreground/60">No sponsorships yet.</div>}
            {sponsors.map((s) => (
              <div key={s.sponsorship_id} className="p-4 border-b border-border last:border-b-0 flex items-baseline justify-between">
                <div>
                  <div className="font-medium">{s.company_name}</div>
                  <div className="text-xs text-foreground/60">{s.contact_email}</div>
                </div>
                <div className="font-display text-xl">{fmtEUR(s.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
